/**
 * Utility functions for external (non-API) fetch calls.
 *
 * These wrap raw fetch() for CDN / S3 interactions that bypass the
 * action orchestrator (which handles backend API calls).
 */

import { logger, LogCategory } from '@/utils/logger';
import { loggedFetch, FetchTarget } from '@/utils/logged-fetch';

/** Fetch and parse JSON from an external URL (e.g. Lottie animation from CDN). */
export async function fetchExternalJson<T = unknown>(url: string): Promise<T> {
  const response = await loggedFetch(
    url,
    {},
    {
      category: LogCategory.NETWORK,
      target: FetchTarget.EXTERNAL_JSON,
    }
  );
  if (!response.ok) {
    throw new Error(
      `External fetch failed: ${response.status} ${response.statusText}`
    );
  }
  return response.json() as Promise<T>;
}

/** Upload a file to a pre-signed S3 URL via PUT. */
export async function uploadToPresignedUrl(
  presignedUrl: string,
  file: File
): Promise<void> {
  const response = await loggedFetch(
    presignedUrl,
    {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    },
    // The logger records host + path only: the presigned query is a secret.
    { category: LogCategory.STORAGE, target: FetchTarget.S3_UPLOAD }
  );

  if (!response.ok) {
    // loggedFetch logs 4xx at warn; keep the historical error severity for S3.
    logger.error('S3 upload failed', { status: response.status });
    throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
  }
}
